import { useState } from 'react'

import moment from 'moment'
import styled, { css } from 'styled-components'

import { Box, Button } from 'app/components/design-system-components'
import { LogDetailsModal } from 'app/components/design-system-components/modals/LogDetailsModal'
import TableComponent from 'app/components/design-system-components/Table'
import { ToolTip } from 'app/components/design-system-components/Tooltip'
import { Text } from 'app/components/design-system-components/typography'
import * as Display from 'app/components/display/display'
import type { EmailLog } from 'app/dashboard/team_activity/EmailReporting/helper'
import { theme } from 'app/match/applicationReportPDF/assets/theme'
import { pdfStandardDisplayTimeFormat } from 'app/sm/helpers'
import {
  convertCourierStatusToMessageStatus,
  extractErrorMessage,
  SUCCESS,
  WARNING,
} from 'app/utils/email/helpers'

const ellipsisStyles = css`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`

const EllipsisText = styled(Text)`
  ${ellipsisStyles}
`

const EllipsisButton = styled(Button)`
  ${ellipsisStyles}
`

const notAvailableElem = (
  <Text
    as="span"
    color={theme.colors.gray400}
    fontSize={theme.fontSizes.pRegular14}
  >
    N/A
  </Text>
)

interface EmailTableProps {
  emailListData: EmailLog[]
}

function EmailTable({ emailListData }: EmailTableProps) {
  const [contentModal, setContentModal] = useState<{
    isOpened: boolean
    config: {
      subject?: string
      body?: string
      recipients?: string
      cc?: string
    }
  }>({
    isOpened: false,
    config: {},
  })

  const setPillType = (status: string) => {
    if (status === SUCCESS) {
      return 'green-hollow'
    } else if (status === WARNING) {
      return 'orange-hollow'
    } else {
      return 'red-hollow'
    }
  }

  const renderContentModal = () => {
    const { isOpened, config } = contentModal
    const closeContentModal = () => {
      setContentModal({ isOpened: false, config: {} })
    }

    if (!isOpened) return

    return (
      <LogDetailsModal
        details={config}
        onDissmiss={closeContentModal}
        onClose={closeContentModal}
      />
    )
  }

  const colDefs = [
    {
      accessor: 'createdAt',
      Header: 'Date',
      Cell: ({ value: creationDate }: { value: string }) => {
        if (!creationDate) return notAvailableElem
        const reportDate = moment(creationDate).format(
          pdfStandardDisplayTimeFormat,
        )
        return (
          <Text width={theme.width[22] + 'px'} as="div">
            {reportDate}
          </Text>
        )
      },
    },
    {
      accessor: 'courierStatus',
      Header: 'Status',
      Cell: ({
        value: status,
        row: { original: email },
      }: {
        value: string
        row: {
          original: EmailLog
        }
      }) => {
        if (!status) return notAvailableElem
        const statusTooltipID = `email_status_${email.guidID}`

        return (
          <Box mb={1}>
            <Display.StatusPill
              pillType={setPillType(
                convertCourierStatusToMessageStatus(status),
              )}
              text={convertCourierStatusToMessageStatus(status)}
              pillTextClass="fw500 fs16 pl15 pr15"
              tooltipID={statusTooltipID}
              textTransform={undefined}
            />
            {email.courierError && (
              <ToolTip
                toolTipID={statusTooltipID}
                content={<span>{extractErrorMessage(email.courierError)}</span>}
                hideQuestionMark
              />
            )}
          </Box>
        )
      },
    },
    {
      accessor: 'recipient',
      Header: 'Recipient(s)',
      Cell: ({ value: recipient }: { value: string }) => {
        if (!recipient) return notAvailableElem

        return (
          <EllipsisText width={theme.width[30] + 'px'}>
            {recipient
              .replace(/\s*<[^>]+>\s*/, ' ')
              .replace(/\s*,\s*/, ', ')
              .trim()}
          </EllipsisText>
        )
      },
    },
    {
      accessor: 'messageType',
      Header: 'Message Type',
      Cell: ({ value: type }: { value: string }) => {
        if (!type) return notAvailableElem

        return <Text as="span">{type}</Text>
      },
    },
    {
      accessor: 'subject',
      Header: 'Message',
      Cell: ({
        value: subject,
        row: { original: email },
      }: {
        value: string
        row: {
          original: EmailLog
        }
      }) => {
        if (!subject) return notAvailableElem

        return (
          <EllipsisButton
            width={theme.width[30] + 'px'}
            variant="linkBlue"
            onClick={() =>
              setContentModal({
                isOpened: true,
                config: {
                  body: email.message,
                  subject: subject,
                  recipients: email.recipient,
                  cc: email.cc,
                },
              })
            }
          >
            {subject}
          </EllipsisButton>
        )
      },
    },
  ]

  return (
    <>
      <Box width="100%" overflowX="auto">
        <TableComponent
          columns={colDefs}
          headerBgColor={theme.colors.white}
          dataToBeEntered={emailListData}
          textColor={theme.colors.gray600}
          showHeaderBottomBorder
          showRowBottomBorder
          thPadding={`${theme.space[5]}px`}
          fontSize={theme.fontSizes.pRegular14}
          verticalAlignTop
        />
      </Box>
      {renderContentModal()}
    </>
  )
}

export default EmailTable
